import type { QuestionData, QuestionSerialized } from '@repo/types/questions/Question';
import type { IQuestion } from '@repo/types/questions/Question.interface';

import { useCallback, useMemo } from 'react';

import { useQuizResolverStore } from '@/store/quiz-store/quizResolverStore';

export interface QuestionState<T extends IQuestion> {
  setAnswer: T['setAnswer'];
  resetAnswer: T['resetAnswer'];
  getAnswer: T['getAnswer'];
  getContent: T['getContent'];
  getMarked: T['getMarked'];
  isMarked: T['isMarked'];
  exportedAnswer: T['exportAnswer'];
}

/**
 * React hook to manage the state and actions for a single quiz question.
 *
 * @template T - The Question class type.
 * @template A - The answer JSON type, defaults to the return type of T['toJSON'].
 * @param questionData - The raw data for the question.
 * @param sectionIndex - The index of the section containing the question.
 * @param questionIndex - The index of the question within the section.
 * @returns An object with the current answer JSON, and methods to set/reset/get the answer and content.
 */
export function useQuestionState<T extends IQuestion>(questionData: QuestionData, sectionIndex: number, questionIndex: number): QuestionState<T> {
  const updateQuestionAnswer = useQuizResolverStore((state) => state.updateQuestionAnswer);
  const getQuestionInstance = useQuizResolverStore((state) => state.getQuestionInstance);

  const selectedAnswer = useQuizResolverStore((state) => state.serializedContent[sectionIndex]?.[questionIndex]?.selectedAnswer);

  const marked = useQuizResolverStore((state) => state.serializedContent[sectionIndex]?.[questionIndex]?.marked);

  const question = useMemo<T>((): T => {
    return getQuestionInstance<T>(sectionIndex, questionIndex);
  }, [sectionIndex, questionIndex, questionData, selectedAnswer, marked]);

  const setAnswer: T['setAnswer'] = useCallback(
    (answer) => {
      const currentQuestion = getQuestionInstance<T>(sectionIndex, questionIndex);
      currentQuestion.setAnswer(answer);
      updateQuestionAnswer(sectionIndex, questionIndex, currentQuestion.toJSON() as QuestionSerialized);
    },
    [getQuestionInstance, questionIndex, sectionIndex, updateQuestionAnswer]
  );

  const resetAnswer: T['resetAnswer'] = useCallback(() => {
    const currentQuestion = getQuestionInstance<T>(sectionIndex, questionIndex);
    currentQuestion.resetAnswer();
    updateQuestionAnswer(sectionIndex, questionIndex, currentQuestion.toJSON() as QuestionSerialized);
  }, [getQuestionInstance, questionIndex, sectionIndex, updateQuestionAnswer]);

  const getContent: T['getContent'] = useCallback(() => {
    return question.getContent();
  }, [question, selectedAnswer]);

  const getMarked: T['getMarked'] = useCallback(() => {
    return question.getMarked();
  }, [question, selectedAnswer, marked]);

  const isMarked: T['isMarked'] = useCallback(() => {
    return question.isMarked();
  }, [question, selectedAnswer, marked]);

  const getAnswer: T['getAnswer'] = useCallback(() => {
    return question.getAnswer();
  }, [question, selectedAnswer, marked]);

  const exportedAnswer: T['exportAnswer'] = useCallback(() => {
    return question.exportAnswer();
  }, [question, selectedAnswer, marked]);

  return {
    setAnswer,
    resetAnswer,
    getAnswer,
    getMarked,
    isMarked,
    getContent,
    exportedAnswer,
  };
}
